import { hourMs, minuteMs } from 'components/date_time/time';

// Free of data-layer imports (sdClient, react-query, utils/node) so the V4 form components
// can read these types and constants without pulling the heavy useV4SensorSettings module
// into a load-time import cycle.

export type CollectionSetting =
    | { enabled: false }
    | {
          enabled: true;
          samplingFrequencyHz: number;
          numberOfSamples: number;
      };

export type UpdateInterval =
    | { mode: 'smartsample' }
    | {
          mode: 'triggered';
          thresholdG: number;
          collectionOffsetMs: number;
          minTimeBetweenTriggersMs: number;
      }
    | { mode: 'timed'; intervalMs: number };

export type UpdateIntervalMode = UpdateInterval['mode'];

export const UPDATE_INTERVAL_MODE_LABELS: Record<UpdateIntervalMode, string> = {
    smartsample: 'SMARTsample',
    triggered: 'Triggered',
    timed: 'Timed',
};

export type CollectionPeriodsSec = {
    envelopedData: number;
    ultrasoundEnveloping: number;
    turningSpeed: number;
};

export type V4SensorSettings = {
    vibrationX: boolean;
    vibrationY: boolean;
    vibrationZ: boolean;
    samplingFrequencyHz: number;
    numberOfSamples: number;
    lowFrequencyCollection: CollectionSetting;
    updateInterval: UpdateInterval;
    envelopedData: CollectionSetting;
    ultrasoundEnveloping: boolean;
    turningSpeed: boolean;
    turningSpeedManuallyDisabled: boolean;
    collectionPeriodsSec: Readonly<CollectionPeriodsSec>;
};

// The override flag is only meaningful in relation to the toggle itself, so both fields are
// derived from a single boolean here rather than being set independently at each call site.
export function turningSpeedToggleValues(
    checked: boolean,
): Pick<V4SensorSettings, 'turningSpeed' | 'turningSpeedManuallyDisabled'> {
    return {
        turningSpeed: checked,
        turningSpeedManuallyDisabled: !checked,
    };
}

export const MAX_COMBINED_NUMBER_OF_SAMPLES = 8000;

export const DEFAULT_LFC_ENABLED: Extract<CollectionSetting, { enabled: true }> = {
    enabled: true,
    samplingFrequencyHz: 2000,
    numberOfSamples: 2000,
};

export const DEFAULT_TRIGGERED: Extract<UpdateInterval, { mode: 'triggered' }> = {
    mode: 'triggered',
    thresholdG: 1,
    collectionOffsetMs: 0,
    minTimeBetweenTriggersMs: 10 * minuteMs,
};

export const DEFAULT_TIMED: Extract<UpdateInterval, { mode: 'timed' }> = {
    mode: 'timed',
    intervalMs: hourMs,
};

export const DEFAULT_ENVELOPED: Extract<CollectionSetting, { enabled: true }> = {
    enabled: true,
    samplingFrequencyHz: 1000,
    numberOfSamples: 2000,
};

export const DEFAULT_COLLECTION_PERIOD_SECONDS = 10 * 60;

export const defaultV4SensorSettings: V4SensorSettings = {
    vibrationX: true,
    vibrationY: true,
    vibrationZ: true,
    samplingFrequencyHz: 16000,
    numberOfSamples: 6000,
    lowFrequencyCollection: { enabled: false },
    updateInterval: { mode: 'smartsample' },
    envelopedData: DEFAULT_ENVELOPED,
    ultrasoundEnveloping: true,
    turningSpeed: false,
    turningSpeedManuallyDisabled: false,
    collectionPeriodsSec: {
        envelopedData: DEFAULT_COLLECTION_PERIOD_SECONDS,
        ultrasoundEnveloping: DEFAULT_COLLECTION_PERIOD_SECONDS,
        turningSpeed: DEFAULT_COLLECTION_PERIOD_SECONDS,
    },
};
